import { createContext, FC, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useFragment } from 'react-relay/hooks';
import { appSubscriptionCheck$key } from 'src/components/__generated__/appSubscriptionCheck.graphql';
import { appSubscriptionCheckFragment } from 'src/components/appSubscriptionCheck';
import { FeatureFlagNames } from 'src/generated/feature-flags';
import { useAppState } from 'src/shared/appState';
import { useFeatureFlagWithConditions } from 'src/shared/feature-flags';
import { useAccountData } from 'src/shared/hooks/useAccountData';
import { OPEN_AGENT_GUIDE_EVENT } from '../inlineHelpLayout/inlineHelpLayout';
import { clearStoredAgentConversation } from './useAgentSession';

/** Options for a conversation started in the sidebar. */
export interface AgentSidebarOptions {
    /** Who started the conversation: that caller can reopen it (see openSidebar) or discard it (see discardSidebarConversation). */
    key?: string;
    /** The drawer caption under "Zakeke AI", when the conversation has a specific purpose. */
    caption?: string;
    /** Called, with the sidebar closed, once the agent has created the product of an approved plan (null when its ID is unknown). */
    onProductCreated?: (productId: number | null) => void;
}

export interface AgentGuideRequest {
    articleId: string;
    requestKey: number;
}

interface AgentAssistantContextValue {
    isEnabled: boolean;
    isOpen: boolean;
    isFullScreen: boolean;
    animateFullScreenEntry: boolean;
    /** Message a caller asked the agent to start from; consumed by the launcher when it opens a new conversation. */
    initialPrompt?: string;
    /** Changes on every prompt request: the launcher remounts the assistant with it, so each prompt starts a new conversation. */
    promptRequestKey: number;
    /** Called once the prompt has been sent, so starting a new conversation later does not send it again. */
    clearInitialPrompt: () => void;
    /** Opens the sidebar on the stored conversation; with options, as the conversation their caller started (same key). */
    openSidebar: (options?: AgentSidebarOptions) => void;
    /** Opens the sidebar on a brand-new conversation that starts from this message; the stored conversation is not restored. */
    openSidebarWithPrompt: (prompt: string, options?: AgentSidebarOptions) => void;
    /** Set by the caller of openSidebarWithPrompt for that conversation: its caption and what to do with the product it creates. */
    sidebarOptions: AgentSidebarOptions | null;
    /** Forgets the conversation started by this caller (e.g. on leaving the page it was about): the next one starts anew. */
    discardSidebarConversation: (key: string) => void;
    openAiMode: () => void;
    /** Opens AI mode on a brand-new conversation that starts from this message; the stored conversation is not restored. */
    startWithPrompt: (prompt: string) => void;
    openClassicMode: () => void;
    close: () => void;
    changeFullScreen: (isFullScreen: boolean) => void;
    guideRequest: AgentGuideRequest | null;
}

const AgentAssistantContext = createContext<AgentAssistantContextValue | null>(null);

export const AgentAssistantProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const { userInfo } = useAccountData();
    const { queryData } = useAppState();
    const subscriptionCheck = useFragment<appSubscriptionCheck$key>(appSubscriptionCheckFragment, queryData?.appSubscriptionCheck ?? null);
    const [isOpen, setIsOpen] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [animateFullScreenEntry, setAnimateFullScreenEntry] = useState(false);
    const [guideRequest, setGuideRequest] = useState<AgentGuideRequest | null>(null);
    const [initialPrompt, setInitialPrompt] = useState<string>();
    const [promptRequestKey, setPromptRequestKey] = useState(0);
    const [sidebarOptions, setSidebarOptions] = useState<AgentSidebarOptions | null>(null);
    const isFullScreenRef = useRef(isFullScreen);
    isFullScreenRef.current = isFullScreen;
    const featureFlagContext = { userId: userInfo?.userID?.toString() };
    // appSubscriptionCheck queries only active subscriptions; a trial is included in this result.
    const hasActiveSubscription = Boolean(subscriptionCheck?.subscription && !subscriptionCheck.subscription.isCancelled);
    const isEnabledByFeatureFlag = useFeatureFlagWithConditions(FeatureFlagNames.BOAIAGENT, featureFlagContext);
    const isEnabled = isEnabledByFeatureFlag && hasActiveSubscription;
    const shouldStartWithAiAgent = useFeatureFlagWithConditions(FeatureFlagNames.START_WITH_AIAGENT, featureFlagContext);

    const openSidebar = useCallback((options?: AgentSidebarOptions) => {
        setSidebarOptions(options ?? null);
        setGuideRequest(null);
        setInitialPrompt(undefined);
        setAnimateFullScreenEntry(false);
        setIsFullScreen(false);
        setIsOpen(true);
    }, []);

    const sidebarOptionsRef = useRef(sidebarOptions);
    sidebarOptionsRef.current = sidebarOptions;
    const discardSidebarConversation = useCallback((key: string) => {
        if (sidebarOptionsRef.current?.key !== key) return;
        clearStoredAgentConversation();
        setSidebarOptions(null);
        setIsOpen((open) => (isFullScreenRef.current ? open : false));
    }, []);

    const openSidebarWithPrompt = useCallback((prompt: string, options?: AgentSidebarOptions) => {
        // The sidebar mounts its assistant on open: without a stored conversation it starts empty and sends the prompt.
        clearStoredAgentConversation();
        setSidebarOptions(options ?? null);
        setGuideRequest(null);
        setInitialPrompt(prompt);
        setPromptRequestKey((key) => key + 1);
        setAnimateFullScreenEntry(false);
        setIsFullScreen(false);
        setIsOpen(true);
    }, []);

    useEffect(() => {
        const openGuide = (event: Event) => {
            const articleId = (event as CustomEvent<{ articleId?: string }>).detail?.articleId;
            if (!articleId || !/^\d+$/.test(articleId)) return;
            setSidebarOptions(null);
            setInitialPrompt(undefined);
            setGuideRequest({ articleId, requestKey: Date.now() });
            setAnimateFullScreenEntry(false);
            setIsFullScreen(false);
            setIsOpen(true);
        };
        window.addEventListener(OPEN_AGENT_GUIDE_EVENT, openGuide);
        return () => window.removeEventListener(OPEN_AGENT_GUIDE_EVENT, openGuide);
    }, []);

    const openAiMode = useCallback(() => {
        setSidebarOptions(null);
        setGuideRequest(null);
        setInitialPrompt(undefined);
        setAnimateFullScreenEntry(true);
        setIsFullScreen(true);
        setIsOpen(true);
    }, []);

    const startWithPrompt = useCallback((prompt: string) => {
        // Full screen mounts its own assistant: without a stored conversation (e.g. one open in the sidebar) it starts empty and sends the prompt.
        clearStoredAgentConversation();
        setSidebarOptions(null);
        setGuideRequest(null);
        setInitialPrompt(prompt);
        setPromptRequestKey((key) => key + 1);
        setAnimateFullScreenEntry(true);
        setIsFullScreen(true);
        setIsOpen(true);
    }, []);

    const openClassicMode = useCallback(() => {
        setAnimateFullScreenEntry(false);
        setIsFullScreen(false);
        setIsOpen(false);
    }, []);

    const clearInitialPrompt = useCallback(() => setInitialPrompt(undefined), []);

    const close = useCallback(() => {
        // Dropped here so reopening the agent later does not replay a prompt the user already sent.
        setInitialPrompt(undefined);

        if (isFullScreen) {
            openClassicMode();
            return;
        }

        setIsOpen(false);
    }, [isFullScreen, openClassicMode]);

    const changeFullScreen = useCallback((fullScreen: boolean) => {
        setAnimateFullScreenEntry(fullScreen);
        setIsFullScreen(fullScreen);
    }, []);

    useEffect(() => {
        if (!isEnabled) {
            setAnimateFullScreenEntry(false);
            setIsFullScreen(false);
            setIsOpen(false);
            return;
        }

        if (shouldStartWithAiAgent) {
            setAnimateFullScreenEntry(false);
            setIsFullScreen(true);
            setIsOpen(true);
        }
    }, [isEnabled, shouldStartWithAiAgent]);

    const value = useMemo<AgentAssistantContextValue>(
        () => ({
            isEnabled,
            isOpen,
            isFullScreen,
            animateFullScreenEntry,
            initialPrompt,
            promptRequestKey,
            clearInitialPrompt,
            openSidebar,
            openSidebarWithPrompt,
            sidebarOptions,
            discardSidebarConversation,
            openAiMode,
            startWithPrompt,
            openClassicMode,
            close,
            changeFullScreen,
            guideRequest
        }),
        [
            animateFullScreenEntry,
            changeFullScreen,
            clearInitialPrompt,
            close,
            guideRequest,
            initialPrompt,
            isEnabled,
            isFullScreen,
            isOpen,
            openAiMode,
            openClassicMode,
            openSidebar,
            openSidebarWithPrompt,
            sidebarOptions,
            discardSidebarConversation,
            promptRequestKey,
            startWithPrompt
        ]
    );

    return <AgentAssistantContext.Provider value={value}>{children}</AgentAssistantContext.Provider>;
};

export const useAgentAssistant = () => {
    const context = useContext(AgentAssistantContext);
    if (!context) throw new Error('useAgentAssistant must be used within an AgentAssistantProvider');
    return context;
};
